import { useState } from 'react';
import type { CaptureForm } from '../../../mocks/leadCapture';

interface Props {
  form: CaptureForm;
  onClose: () => void;
}

type EmbedTab = 'html' | 'js' | 'wordpress';

export default function EmbedCodeModal({ form, onClose }: Props) {
  const [tab, setTab] = useState<EmbedTab>('js');
  const [copied, setCopied] = useState<string | null>(null);

  const formSlug = form.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  const baseUrl = 'https://app.horizontravel.ae';
  const formId = form.id;

  const jsSnippet = `<!-- Horizon Travel Lead Capture — ${form.name} -->
<div id="ht-form-${formId}"></div>
<script>
  (function() {
    var s = document.createElement('script');
    s.src = '${baseUrl}/embed/form.js';
    s.async = true;
    s.setAttribute('data-form-id', '${formId}');
    s.setAttribute('data-container', 'ht-form-${formId}');
    s.setAttribute('data-color', '${form.color}');
    document.head.appendChild(s);
  })();
</script>`;

  const htmlSnippet = `<!-- Horizon Travel Lead Capture — ${form.name} -->
<iframe
  src="${baseUrl}/embed/${formSlug}?id=${formId}"
  width="100%"
  height="600"
  frameborder="0"
  style="border-radius:12px; border:1px solid #e5e7eb;"
  title="${form.name}"
  loading="lazy"
></iframe>`;

  const wordpressSnippet = `[horizon_form id="${formId}" color="${form.color}" title="${form.name}"]

/* Or use the PHP shortcode in your theme: */
<?php echo do_shortcode('[horizon_form id="${formId}"]'); ?>`;

  const snippets: Record<EmbedTab, string> = {
    js: jsSnippet,
    html: htmlSnippet,
    wordpress: wordpressSnippet,
  };

  const copy = (key: string, text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${form.color}18` }}>
              <i className="ri-code-s-slash-line text-base" style={{ color: form.color }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Embed Form</h2>
              <p className="text-xs text-gray-400">{form.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {/* Status check */}
          <div className={`flex items-center gap-3 p-3 rounded-xl mb-5 ${form.status === 'Active' ? 'bg-emerald-50 border border-emerald-100' : 'bg-amber-50 border border-amber-100'}`}>
            <i className={`${form.status === 'Active' ? 'ri-checkbox-circle-line text-emerald-500' : 'ri-error-warning-line text-amber-500'} text-lg`} />
            <div>
              <p className={`text-sm font-medium ${form.status === 'Active' ? 'text-emerald-800' : 'text-amber-800'}`}>
                {form.status === 'Active' ? 'Form is live and accepting responses' : `Form is ${form.status.toLowerCase()} — activate it before embedding`}
              </p>
              <p className="text-xs text-gray-500">Form ID: <span className="font-mono">{formId}</span></p>
            </div>
          </div>

          {/* Embed type tabs */}
          <div className="flex gap-2 mb-4">
            {([
              { key: 'js', label: 'JavaScript Snippet', icon: 'ri-javascript-line', desc: 'Recommended' },
              { key: 'html', label: 'iFrame Embed', icon: 'ri-html5-line', desc: 'Any platform' },
              { key: 'wordpress', label: 'WordPress', icon: 'ri-wordpress-line', desc: 'Shortcode' },
            ] as const).map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border text-center cursor-pointer transition-all ${
                  tab === t.key ? 'border-gray-400 bg-gray-50' : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <i className={`${t.icon} text-lg ${tab === t.key ? 'text-gray-800' : 'text-gray-400'}`} />
                <span className={`text-xs font-medium ${tab === t.key ? 'text-gray-800' : 'text-gray-500'}`}>{t.label}</span>
                <span className="text-xs text-gray-400">{t.desc}</span>
              </button>
            ))}
          </div>

          {/* Code block */}
          <div className="relative">
            <div className="absolute top-3 right-3 z-10">
              <button
                onClick={() => copy(tab, snippets[tab])}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-all"
                style={{ backgroundColor: copied === tab ? '#10b981' : form.color, color: 'white' }}
              >
                <i className={`${copied === tab ? 'ri-check-line' : 'ri-file-copy-line'}`} />
                {copied === tab ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="bg-gray-900 text-gray-100 p-5 rounded-xl text-xs leading-relaxed overflow-x-auto whitespace-pre-wrap">
              <code>{snippets[tab]}</code>
            </pre>
          </div>

          {/* Instructions */}
          <div className="mt-5 bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-600 mb-3">
              {tab === 'js' ? 'How to add to your website:' : tab === 'html' ? 'iFrame integration guide:' : 'WordPress plugin setup:'}
            </p>
            <ol className="space-y-2">
              {tab === 'js' && [
                'Copy the JavaScript snippet above',
                'Paste it anywhere in your website\'s HTML body — sidebar, contact page, landing page, or popup',
                'The form will automatically render with your brand colors and collect responses',
                'New submissions appear in your Responses inbox instantly',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                  <span className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-semibold" style={{ backgroundColor: form.color }}>{i + 1}</span>
                  {step}
                </li>
              ))}
              {tab === 'html' && [
                'Copy the iFrame code above',
                'Paste into your page\'s HTML editor',
                'Adjust the height value (600px) to fit your layout',
                'Works on any website — Wix, Squarespace, custom HTML',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                  <span className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-semibold" style={{ backgroundColor: form.color }}>{i + 1}</span>
                  {step}
                </li>
              ))}
              {tab === 'wordpress' && [
                'Install the "Horizon Travel Forms" plugin from your WordPress dashboard',
                'Go to Plugins → Add New → search "Horizon Travel"',
                'Activate the plugin and enter your Agency API Key in Settings',
                'Use the shortcode above in any page, post, or widget',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                  <span className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-semibold" style={{ backgroundColor: form.color }}>{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>

          {/* Direct link */}
          <div className="mt-4 flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
            <i className="ri-link text-gray-400" />
            <p className="text-xs text-gray-500 font-mono flex-1 truncate">{baseUrl}/forms/{formSlug}</p>
            <button
              onClick={() => copy('link', `${baseUrl}/forms/${formSlug}`)}
              className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
            >
              {copied === 'link' ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
